import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Table, TableWrapper } from "@/components/ui/table";
import { EmptyRow, Panel } from "@/features/oversight/oversight-ui";
import { WipHint } from "@/components/ui/wip-hint";

/**
 * Ledger Comparison (RCV) — the Alloy ledger against custody providers and bank
 * records for the latest completed run.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/reconciliation/
 * ledger-comparison/page.tsx` → `features/oversight/reconciliation.tsx`
 * (`LedgerComparison`): breadcrumb, page header, and a stack of panels whose body
 * is an asset table.
 *
 * No data is ported. The design built one panel per comparison card
 * (`WLT-001 · HT Operations Hot Wallet`, …) from `LEDGER_COMPARISON` in
 * `features/oversight/oversight-data.ts` — fictional wallets, providers and
 * balances. That service does not exist in this environment: `alloy-reconflow-service`
 * is a README with no pom.xml, no src/ and no migrations (page-delivery-pipeline.md
 * G2 `defer`; context/page_pipeline/BACKEND_GAPS.md rows 20-23), so there is no
 * comparison to render and none of the design's sample values are carried over.
 *
 * What stays is the structure: the design's panel primitive and table chrome, with
 * the table body holding the honest not-available state instead of rows. The column
 * headers are the generic roles the design names in its own subtitle (the design's
 * third header is the provider's *name* on each card — data, so it is generalised
 * here, never invented per provider). One column set is shown once rather than
 * repeated per fabricated wallet: with zero comparisons there are zero cards.
 *
 * The page calls no endpoint (`src/lib/api.ts` is not imported) and has no controls
 * that would need one.
 */
export default function ReconciliationLedgerComparisonPage() {
  return (
    <div data-testid="ledger-comparison-page">
      <Breadcrumb
        items={[{ label: "Reconciliation", href: "/reconciliation" }, { label: "Ledger Comparison" }]}
      />

      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Ledger Comparison</h1>
          <WipHint />
        </div>
        <p className="mt-1 text-sm text-muted">
          Balances as recorded on the Alloy ledger, custody providers, and bank records for the latest
          completed run.
        </p>
        <p className="mt-1 text-sm text-muted" data-testid="wip-note">
          This area has no backend in this environment yet.
        </p>
      </div>

      <Panel title="Ledger comparison" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-5 py-2.5">Asset</th>
                <th className="px-5 py-2.5">Alloy ledger</th>
                <th className="px-5 py-2.5">Custody provider</th>
                <th className="px-5 py-2.5">Bank ledger</th>
              </tr>
            </thead>
            <tbody>
              <EmptyRow colSpan={4}>
                <div role="status" className="mx-auto flex max-w-[560px] flex-col items-center gap-1">
                  <span className="text-sm font-semibold text-ink">
                    Ledger comparison is not available in this environment yet
                  </span>
                  <span className="text-sm leading-relaxed text-muted">
                    The reconciliation service does not exist in this environment yet. Nothing on this
                    page reflects a real comparison of the Alloy ledger against custody providers or
                    on-chain sources.
                  </span>
                </div>
              </EmptyRow>
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
    </div>
  );
}
